import { bytesEqual, wallNow } from '@casa/util';
import { parseSps, toAnnexB, type SpsInfo } from './h264.ts';
import type { AccessUnit } from './h264_depacketizer.ts';

export type HardwarePreference = 'prefer-hardware' | 'prefer-software';

export interface DecoderStats {
    /** The configuration given to `VideoDecoder.configure`; null until the first key picture. */
    config: { codec: string; codedWidth: number; codedHeight: number; hardwareAcceleration: HardwarePreference } | null;
    /**
     * Which kind of decoder produces the pictures; null until the first one is decoded. This is
     * the preference the decoder was configured with: Chromium offers only hardware decoders for
     * `prefer-hardware` and only software ones for `prefer-software`.
     */
    acceleration: 'hardware' | 'software' | null;
    /** Why the hardware decoder was given up, when it was. */
    softwareFallbackReason: string | null;
    /** Pixel format and size of the last decoded picture. */
    frameFormat: string | null;
    displayWidth: number;
    displayHeight: number;
    framesDecoded: number;
    /** `wallNow()` at the first decoded picture; null until then. */
    firstFrameTime: number | null;
    /** Pictures decoded in the last second. */
    fps: number;
    /**
     * Time from the arrival of a picture's last packet in the app to the picture leaving the
     * decoder: mean over the last second and the largest value so far. Null without arrival times.
     */
    latencyMs: number | null;
    latencyMaxMs: number | null;
    /** Pictures not decoded because the decoder was behind or waiting for a key picture. */
    droppedAccessUnits: number;
    /** Times the decoder was behind and playback jumped to the next key picture. */
    skipsToKeyFrame: number;
    decodeQueueSize: number;
    decoderErrors: number;
    lastError: string | null;
}

export interface H264DecoderOptions {
    /** More waiting pictures than this means decoding is behind. Default 8. */
    maxDecodeQueue?: number;
    /**
     * The same limit while a fresh decoder has not produced its first picture. Default 75. A
     * hardware decoder can take most of a second to start (857 ms seen on a Galaxy Tab A9+); the
     * pictures that arrive meanwhile are not a sign of a slow decoder.
     */
    maxStartupQueue?: number;
    /** RTP clock rate of the access unit timestamps. Default 90000. */
    clockRate?: number;
    log?: (message: string) => void;
}

/** Receives every decoded picture and owns it: it must close the frame or transfer it. */
export type FrameSink = (frame: VideoFrame) => void;

/** A sink that paints every picture to a canvas, sized to the picture, and closes it. */
export function canvasSink(canvas: HTMLCanvasElement | OffscreenCanvas) : FrameSink {
    const context = (canvas as HTMLCanvasElement).getContext('2d', { alpha: false });
    if (!context) {
        throw new Error('the canvas has no 2D context');
    }

    return (frame) => {
        try {
            if (canvas.width !== frame.displayWidth || canvas.height !== frame.displayHeight) {
                canvas.width = frame.displayWidth;
                canvas.height = frame.displayHeight;
            }
            context.drawImage(frame, 0, 0, frame.displayWidth, frame.displayHeight);
        } finally {
            // Always: an unclosed frame holds on to a decoder buffer.
            frame.close();
        }
    };
}

/**
 * Decodes H.264 access units with WebCodecs and hands every picture to a sink. Hardware decoding
 * is asked for first; if the engine refuses the configuration, the next key picture is tried
 * with a software decoder. When decoding falls behind, pictures are dropped up to the next key
 * picture instead of building up latency.
 */
export class H264Decoder {
    readonly #sink: FrameSink;
    readonly #maxDecodeQueue: number;
    readonly #maxStartupQueue: number;
    readonly #clockRate: number;
    readonly #log: (message: string) => void;
    #decoder: VideoDecoder | null = null;
    #preference: HardwarePreference = 'prefer-hardware';
    #sps: Uint8Array | null = null;
    #info: SpsInfo | null = null;
    #waitForKey = true;
    #decodedSinceConfigure = 0;
    /** Arrival time of the pictures inside the decoder, by chunk timestamp. */
    readonly #arrivals = new Map<number, number>();
    #outputs: { time: number; latency: number | null }[] = [];
    #closed = false;
    readonly #counters = {
        acceleration: null as DecoderStats['acceleration'],
        softwareFallbackReason: null as string | null,
        frameFormat: null as string | null,
        displayWidth: 0,
        displayHeight: 0,
        framesDecoded: 0,
        firstFrameTime: null as number | null,
        latencyMaxMs: null as number | null,
        droppedAccessUnits: 0,
        skipsToKeyFrame: 0,
        decoderErrors: 0,
        lastError: null as string | null,
    };

    constructor(sink: FrameSink, options: H264DecoderOptions = {}) {
        this.#sink = sink;
        this.#maxDecodeQueue = options.maxDecodeQueue ?? 8;
        this.#maxStartupQueue = options.maxStartupQueue ?? 75;
        this.#clockRate = options.clockRate ?? 90_000;
        this.#log = options.log ?? (() => {});
    }

    push(unit: AccessUnit) : void {
        if (this.#closed) {
            return;
        }
        if (unit.key && unit.sps && unit.pps) {
            const changed = this.#sps === null || !bytesEqual(this.#sps, unit.sps);
            const behind = !this.#waitForKey && this.#decoder !== null && this.#decoder.decodeQueueSize > this.#queueLimit();
            if (behind) {
                this.#skipped(this.#decoder!.decodeQueueSize);
            }
            // A fresh decoder also throws away whatever the old one still had queued.
            if (changed || !this.#decoder || this.#waitForKey || behind) {
                if (!this.#configure(unit.sps, changed)) {
                    this.#counters.droppedAccessUnits++;

                    return;
                }
            }
            this.#waitForKey = false;
        } else if (!this.#decoder || this.#waitForKey) {
            this.#counters.droppedAccessUnits++;

            return;
        } else if (this.#decoder.decodeQueueSize > this.#queueLimit()) {
            // Behind: stop feeding the decoder; the next key picture resets it and starts over.
            this.#waitForKey = true;
            this.#skipped(this.#decoder.decodeQueueSize);
            this.#counters.droppedAccessUnits++;

            return;
        }

        const nalus = unit.key && unit.sps && unit.pps ? [unit.sps, unit.pps, ...unit.nalus] : unit.nalus;
        const timestamp = Math.round((unit.timestamp * 1_000_000) / this.#clockRate);
        if (unit.receivedAt !== undefined) {
            this.#arrivals.set(timestamp, unit.receivedAt);
        }
        try {
            // No `description` in the configuration, so the data is an Annex B stream.
            this.#decoder!.decode(
                new EncodedVideoChunk({ type: unit.key ? 'key' : 'delta', timestamp, data: toAnnexB(nalus) }),
            );
        } catch (error) {
            this.#fail(error);
        }
    }

    #skipped(queue: number) : void {
        this.#counters.skipsToKeyFrame++;
        this.#log(`video: decoder behind (${queue} pictures waiting), skipping to the next key picture`);
    }

    #queueLimit() : number {
        return this.#decodedSinceConfigure === 0 ? this.#maxStartupQueue : this.#maxDecodeQueue;
    }

    stats() : DecoderStats {
        const now = wallNow();
        this.#outputs = this.#outputs.filter((output) => now - output.time <= 1000);
        const latencies = this.#outputs.flatMap((output) => (output.latency === null ? [] : [output.latency]));

        return {
            config: this.#info && {
                codec: this.#info.codec,
                codedWidth: this.#info.width,
                codedHeight: this.#info.height,
                hardwareAcceleration: this.#preference,
            },
            ...this.#counters,
            fps: this.#outputs.length,
            latencyMs: latencies.length === 0 ? null : latencies.reduce((sum, value) => sum + value, 0) / latencies.length,
            decodeQueueSize: this.#decoder?.state === 'configured' ? this.#decoder.decodeQueueSize : 0,
        };
    }

    close() : void {
        this.#closed = true;
        this.#discardDecoder();
    }

    /** Starts a fresh decoder for the stream described by `sps`. False when that is not possible. */
    #configure(sps: Uint8Array, changed: boolean) : boolean {
        if (changed) {
            try {
                this.#info = parseSps(sps);
                this.#sps = sps;
            } catch (error) {
                this.#counters.lastError = `cannot read the stream parameters: ${String(error)}`;

                return false;
            }
        }
        const info = this.#info!;
        this.#discardDecoder();
        this.#decodedSinceConfigure = 0;
        try {
            const decoder = new VideoDecoder({
                output: (frame) => this.#output(decoder, frame),
                error: (error) => {
                    // Errors of a decoder that was already replaced are of no interest.
                    if (this.#decoder === decoder) {
                        this.#fail(error);
                    }
                },
            });
            decoder.configure({
                codec: info.codec,
                codedWidth: info.width,
                codedHeight: info.height,
                hardwareAcceleration: this.#preference,
                optimizeForLatency: true,
            });
            this.#decoder = decoder;

            return true;
        } catch (error) {
            this.#fail(error);

            return false;
        }
    }

    #output(decoder: VideoDecoder, frame: VideoFrame) : void {
        if (this.#closed || this.#decoder !== decoder) {
            frame.close();

            return;
        }
        const now = wallNow();
        const arrival = this.#arrivals.get(frame.timestamp);
        this.#arrivals.delete(frame.timestamp);
        const latency = arrival === undefined ? null : now - arrival;
        if (latency !== null) {
            this.#counters.latencyMaxMs = Math.max(this.#counters.latencyMaxMs ?? 0, latency);
        }
        this.#decodedSinceConfigure++;
        this.#counters.framesDecoded++;
        this.#counters.acceleration = this.#preference === 'prefer-hardware' ? 'hardware' : 'software';
        this.#counters.frameFormat = frame.format;
        this.#counters.displayWidth = frame.displayWidth;
        this.#counters.displayHeight = frame.displayHeight;
        this.#counters.firstFrameTime ??= now;
        this.#outputs.push({ time: now, latency });
        try {
            this.#sink(frame);
        } catch (error) {
            this.#counters.lastError = `cannot show the picture: ${String(error)}`;
            frame.close(); // harmless when the sink already closed it
        }
    }

    #fail(error: unknown) : void {
        const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        this.#counters.decoderErrors++;
        this.#counters.lastError = message;
        this.#log(`video: decoder error (${message})`);
        // A hardware decoder that never produced a picture is taken as "not available for this stream".
        if (this.#preference === 'prefer-hardware' && this.#decodedSinceConfigure === 0) {
            this.#preference = 'prefer-software';
            this.#counters.softwareFallbackReason = message;
        }
        this.#discardDecoder();
        this.#waitForKey = true;
    }

    #discardDecoder() : void {
        const decoder = this.#decoder;
        this.#decoder = null;
        this.#arrivals.clear();
        if (decoder && decoder.state !== 'closed') {
            try {
                decoder.close();
            } catch {
                // already closed by an error
            }
        }
    }
}
